import React from "react";
import axios from "axios";
import { useRouter } from "@src/lib/router";
import { useDispatch, useSelector } from "react-redux";
import Input from "@src/components/UIElements/Input";
import getAxiosConfig from "@src/utils/getAxiosConfig";
import { getAwsAssets } from "@src/utils";
import styles from "./styles/SavedQuestions.module.scss";
import { BookIcon, DeleteIcon } from "./assets";
import { CONTENT_DEEPLINK_ACTIONS } from "../Content/store/constants";
import { hitContentDeeplinkOld } from "../Content/helpers/hitContentDeeplink";
import { openPullToRefreshAction } from "../../PullToRefresh/store";

const getBookmarkPayload = (apiData) => {
  const list = apiData || []

  const totalCount = list?.length ??
    0;

  return {
    list: Array.isArray(list) ? list : [],
    totalCount: Number(totalCount) || 0,
  };
};

const getQuestionTitle = (item) => item?.question?.nameText || "Untitled Question"
const getQuestionMeta = (item) => item?.testName || "Untitled Test"
const getSolutionUrl = (item) => item?.solutionsUrl || item?.solutionUrl || "";
const getQuestionId = (item) => item?.question._id || "";

const SavedQuestions = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [removingQuestionIds, setRemovingQuestionIds] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState("");
  const [bookmarkList, setBookmarkList] = React.useState([]);
  const [bookmarkCount, setBookmarkCount] = React.useState(0);

  const orgId = useSelector(
    (state) =>
      state?.overviewReducer?.overViewDetails?.course?.metaData?.orgId ||
      state?.persistedDataReducer?.orgDetails?.organization?.id
  );
  const studentId = useSelector(
    (state) => state?.globalReducer?.userDetailsInfo?.studentId 
  );
  const isPullToRefreshTriggered = useSelector(
    (state) => state?.pullToRefreshReducer?.isPullToRefreshTriggered
  );

  const resolvedCourseId = router?.query?.courseId;
  const resolvedOrgId = orgId || router?.query?.orgId;

  const fetchBookmarkedQuestions = async () => {
    if (!resolvedCourseId || !resolvedOrgId || !studentId) {
      if (isPullToRefreshTriggered) {
        dispatch(openPullToRefreshAction());
      }
      return;
    }
    setLoading(true);
    try {
      const response = await axios(
        getAxiosConfig(
          "GET",
          () =>
            `/v3/courses/studentQuestionBookmarks?courseId=${resolvedCourseId}&orgId=${resolvedOrgId}&studentId=${studentId}`
        )
      );
      const payload = getBookmarkPayload(response?.data?.data);
      setBookmarkList(payload.list);
      setBookmarkCount(payload.totalCount);
    } catch (err) {
      setBookmarkList([]);
      setBookmarkCount(0);
    } finally {
      setLoading(false);
      setIsInitialLoading(false);
      if (isPullToRefreshTriggered) {
        dispatch(openPullToRefreshAction());
      }
    }
  };

  const handleOpenSolution = (question) => {
    const solutionUrl = getSolutionUrl(question);
    const questionId = getQuestionId(question);
    if (!solutionUrl) return;
    const separator = solutionUrl.includes("?") ? "&" : "?";
    const finalSolutionUrl = questionId
      ? `${solutionUrl}${separator}qId=${questionId}`
      : solutionUrl;
    hitContentDeeplinkOld(CONTENT_DEEPLINK_ACTIONS.UTIL_TEST, {
      paramOne: finalSolutionUrl,
    });
  };

  const handleRemoveBookmark = async (question) => {
    const questionId = question?.studentQuestionId;
    if (!questionId) return;
    setRemovingQuestionIds((prev) => [...prev, questionId]);

    try {
      await axios(
        getAxiosConfig("PATCH", () => "/v3/courses/addStudentQuestionBookmark", {
          questionId: question?.studentQuestionId,
          testId: question?.testId,
          studentId: studentId,
          courseId: resolvedCourseId,
          type: "remove",
          questionType: question?.testType || "",
        })
      );

      setBookmarkList((prev) =>
        prev.filter((item) => item?.studentQuestionId !== questionId)
      );
      setBookmarkCount((prev) => Math.max(0, prev - 1));
    } finally {
      setRemovingQuestionIds((prev) => prev.filter((id) => id !== questionId));
    }
  };

  React.useEffect(() => {
    if (router?.query?.tabId == 6) {
      if (resolvedCourseId && resolvedOrgId && studentId) {
        fetchBookmarkedQuestions();
      } else {
        setIsInitialLoading(true);
      }
    }
  }, [router?.query?.tabId, resolvedCourseId, resolvedOrgId, studentId]);

  React.useEffect(() => {
    if (isPullToRefreshTriggered && router?.query?.tabId == 6) {
      fetchBookmarkedQuestions();
    }
  }, [
    isPullToRefreshTriggered,
    router?.query?.tabId,
    resolvedCourseId,
    resolvedOrgId,
    studentId,
  ]);

  const filteredList = React.useMemo(() => {
    const query = searchValue?.trim()?.toLowerCase();
    if (!query) return bookmarkList;
    return bookmarkList?.filter((item) => {
      const searchableText = `${getQuestionTitle(item)} ${getQuestionMeta(item)}`
        .toLowerCase()
        .trim();
      return searchableText.includes(query);
    });
  }, [searchValue, bookmarkList]);

  return (
    <div className={styles.savedQuestions}>
      {isInitialLoading ? (
        <div className={styles.savedQuestions__card}>
          <div className={styles.savedQuestions__shimmer}>
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className={styles.savedQuestions__shimmer__row} />
            ))}
          </div>
        </div>
      ) : (
        <>
      <div className={styles.savedQuestions__search}>
        <Input
          icon={getAwsAssets("searchIcon.svg", "content")}
          type="name"
          placeholder="Search Saved Question"
          value={searchValue}
          onChange={(e) => setSearchValue(e?.target?.value || "")}
        />
      </div>

      <div className={styles.savedQuestions__card}>
        {bookmarkCount > 0 ? (
          <div className={styles.savedQuestions__card__heading}>
            {bookmarkCount} Saved Questions
          </div>
        ) : null}

        {loading ? (
          <div className={styles.savedQuestions__shimmer}>
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className={styles.savedQuestions__shimmer__row} />
            ))}
          </div>
        ) : filteredList?.length ? (
          <div className={styles.savedQuestions__list}>
            {filteredList.map((item, index) => (
              <div
                className={styles.savedQuestions__list__item}
                key={item?.studentQuestionId || item?.question?.id || `${index}_saved_q`}
                onClick={() => handleOpenSolution(item)}
              >
                <div className={styles.savedQuestions__list__item__icon}>
                  <BookIcon width={32} height={32} />
                </div>
                <div className={styles.savedQuestions__list__item__content}>
                  <div className={styles.savedQuestions__list__item__content__title}>
                    {getQuestionTitle(item)}
                  </div>
                  <div className={styles.savedQuestions__list__item__content__meta}>
                    {getQuestionMeta(item)}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.savedQuestions__list__item__action}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveBookmark(item);
                  }}
                  disabled={removingQuestionIds.includes(item?.studentQuestionId)}
                >
                  <DeleteIcon width={13} height={15} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.savedQuestions__empty}>
            No saved questions found.
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default SavedQuestions;
